/**
 * Supabase client singleton.
 *
 * Reads VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY from the environment.
 * If either is missing the client is null — auth features will be disabled
 * and the rest of the app continues to work via localStorage (parallel mode).
 */

import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { getAuthStorage } from '@/lib/native/authStorage';

const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

/**
 * True iff Supabase environment variables are configured.
 * Code that depends on the backend should check this first.
 */
export const isSupabaseConfigured: boolean = Boolean(url && anonKey);

/**
 * The Supabase client. Will be `null` if env vars are missing.
 *
 * Always check `isSupabaseConfigured` before using, OR use the helpers
 * in `lib/auth.ts` which do the check internally.
 */
export const supabase: SupabaseClient | null = isSupabaseConfigured
  ? createClient(url!, anonKey!, {
      auth: {
        // Persist sessions across tabs/reloads (Supabase default behavior)
        persistSession: true,
        // Auto-refresh tokens before they expire
        autoRefreshToken: true,
        // Detect auth callback redirects (e.g., email verification)
        detectSessionInUrl: true,
        // On native, back the session with Capacitor Preferences (UserDefaults)
        // so it survives WKWebView storage eviction. undefined on web => the
        // built-in localStorage adapter is used.
        storage: getAuthStorage(),
      },
    })
  : null;

if (!isSupabaseConfigured && typeof console !== 'undefined') {
  console.warn(
    '[ProjexHQ] Supabase not configured (VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY missing). ' +
      'Auth features disabled; app continues in localStorage mode.',
  );
}
